
import React from 'react';

const LegalLayout = ({ title, children }) => (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-6 text-gray-800">{title}</h1>
        <div className="prose prose-sm sm:prose lg:prose-lg max-w-none text-gray-600">
            {children}
        </div>
    </div>
);

export const TermsPage = () => (
    <LegalLayout title="Terms of Service">
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <p>Welcome to AI T-Shirt Design. By accessing or using our website, you agree to be bound by these Terms of Service.</p>

        <h3>1. Use of Service</h3>
        <p>You agree to use our AI generation tools responsibly and not to generate content that is illegal, offensive, or violates intellectual property rights.</p>

        <h3>2. Accounts</h3>
        <p>You are responsible for safeguarding the password that you use to access the service and for any activities or actions under your password.</p>

        <h3>3. Intellectual Property</h3>
        <p>
          Rights in AI-generated and uploaded artwork depend on your situation and applicable law. You are responsible for ensuring your designs do not infringe third-party rights. For details on AI tools and third-party providers, see our{' '}
          <a href="/ai-tool-terms" className="text-primary font-medium hover:underline">Third-Party AI Tool Terms</a>.
        </p>

        <h3>4. Termination</h3>
        <p>We may terminate or suspend access to our Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.</p>
    </LegalLayout>
);

export const PrivacyPolicyPage = () => (
    <LegalLayout title="Privacy Policy">
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <p>Your privacy is important to us. It is AI T-Shirt Design's policy to respect your privacy regarding any information we may collect from you across our website.</p>

        <h3>1. Information We Collect</h3>
        <p>We collect information you provide directly to us, such as when you create an account, design a t-shirt, or make a purchase. This includes your name, email address, and shipping information.</p>

        <h3>2. How We Use Information</h3>
        <p>We use the information we collect to operate and maintain our services, process your orders, and communicate with you.</p>

        <h3>3. Cookies</h3>
        <p>We use cookies to help you navigate efficiently and perform certain functions. Detailed information about all cookies can be found under each consent category below.</p>
    </LegalLayout>
);

/**
 * Long-form AI / content disclaimer. Keep in sync with counsel; links in Section 5 should be real URLs.
 */
export const AiToolTermsPage = () => (
    <LegalLayout title="Third-Party AI Tool Terms of Use">
        <p className="text-sm text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>

        <p>
          Yourshirt may make available certain artificial intelligence tools, features, and services (&quot;AI Tools&quot;) on its platform for your convenience and benefit.
          These AI Tools may offer suggestions, generate images or text, or provide other information (&quot;Outputs&quot;) based on the prompts, images, or other data you provide (&quot;Inputs&quot;).
        </p>

        <p>
          To provide these AI Tools, we rely on various third-party service providers (&quot;AI Providers&quot;).
          The legal relationship between you and Yourshirt is subject to our Terms of Service and all other relevant Yourshirt policies that apply to Yourshirt users.
        </p>

        <p>
          Your use of any AI Tools is subject to these Third-Party AI Tool Terms of Use (&quot;AI Terms&quot;), which apply in conjunction with our Terms of Service and any additional terms specific to the AI Tool.
          In the event of any conflict between a provision in these AI Terms and a provision in the Terms of Service, the provision in these AI Terms shall prevail with respect to your use of the AI Tools.
          Any capitalized terms used in these AI Terms not defined herein shall have the same meaning as set out in the Yourshirt Terms of Service.
        </p>

        <p className="font-semibold text-gray-800">
          By accessing or using any AI Tool on the Yourshirt platform, you understand, accept, and acknowledge the following:
        </p>

        <h2>1. Third-party AI providers and disclaimers</h2>
        <p>
          Your use of any specific AI Tool is also subject to the usage policies and other relevant terms of the respective AI Provider.
          By using an AI Tool, you agree to the usage policies of the corresponding AI Provider.
          Links to the usage policies for each AI Provider are in Section 5 of these AI Terms.
          It is your responsibility to review the applicable usage policies before using any AI Tool.
        </p>
        <p>
          Yourshirt has no responsibility for or control over the underlying technology of the AI Providers or their Outputs.
          The AI Tools are provided solely for your convenience.
          Yourshirt does not endorse, approve, or guarantee the accuracy, safety, or legality of any Outputs.
          If you choose to use the AI Tools, you assume all associated risks.
          You are solely responsible for evaluating the risks and benefits associated with the use of any Inputs and Outputs.
        </p>
        <p className="text-sm uppercase tracking-wide text-gray-700">
          The AI Tools and any Outputs are provided &quot;as is&quot; and &quot;as available&quot; and are provided without any representations or warranties of any kind, express or implied, including, but not limited to,
          the implied warranties of title, non-infringement, merchantability, and fitness for a particular purpose, and any warranties implied by any course of performance or usage of trade, all of which are expressly disclaimed,
          save to the extent required by law.
          Yourshirt and its directors, employees, agents, representatives, suppliers, partners, and AI Providers do not warrant that: (a) the AI Tools will be secure or available at any particular time or location;
          (b) any defects or errors will be corrected; (c) any content or software available at or through the AI Tools is free of viruses or other harmful components; or (d) the results of using the AI Tools will meet your requirements.
          Your use of the AI Tools is solely at your own risk.
        </p>

        <h2>2. Your responsibility for Inputs and Outputs</h2>
        <p>You are solely responsible for the Inputs you provide and for your use of the Outputs.</p>
        <p>
          Any Output you choose to use is considered your &quot;Content&quot; as defined in our Terms of Service and is subject to our Terms of Service, Intellectual Property Policy, Privacy Policy, and any other applicable terms.
          This includes, but is not limited to:
        </p>
        <ul>
          <li>
            Your responsibility to ensure your Inputs and use of Outputs do not infringe the intellectual property rights (including copyright, trademark, and right of publicity) of any third party.
          </li>
          <li>
            Our right to suspend your account and refuse to produce, block, or remove any products with your Content that we believe may infringe on the rights of others, or that is harmful, hateful, or otherwise in violation of our terms.
          </li>
          <li>
            Our right to disable access or remove allegedly infringing Content from the Yourshirt platform if we receive a valid DMCA notice or other similar legal takedown request.
          </li>
          <li>
            <strong>Copyright of AI-generated content:</strong> You acknowledge that under the laws of many jurisdictions you may not be able to claim copyright ownership in Outputs generated solely by an AI system.
            It is your responsibility to understand and comply with applicable copyright laws regarding your use of Outputs.
          </li>
          <li>
            You must not represent any Output as being entirely human-generated or as a photograph of a real-world event.
            You agree not to mislead others about the AI-generated nature of the content.
          </li>
          <li>
            You must not use the AI Tools for any malicious purpose or to engage in unlawful activity, including but not limited to creating counterfeit products, generating malware, spam, or disinformation,
            or attempting to discover and exploit vulnerabilities in the AI Tools.
          </li>
          <li>You undertake to critically evaluate every Output before using it as part of your Content to ensure such use complies with all applicable laws and Yourshirt&apos;s policies.</li>
        </ul>

        <h2>3. Use of your Content and inherent risks</h2>
        <p>
          To provide the AI Tools, you grant Yourshirt and its AI Providers the necessary, worldwide, non-exclusive, royalty-free rights to use, host, process, and transmit your Inputs and Outputs.
        </p>
        <p>
          Any Input you provide—including any personal data you choose to include—may be transferred to our AI Providers to enable the functionality of the service.
          In relation to any personal data provided to an AI Tool, it may not be technically possible to fully exercise data subject rights (including the right to deletion) in relation to Inputs or Outputs.
          We strongly advise against including any sensitive, confidential, or personal information in your Inputs.
        </p>
        <p>
          The handling of your Inputs and Outputs by our AI Providers, including whether they are used to improve their services, is governed by the contractual terms with each AI Provider.
          Some providers may be contractually prohibited from using your content to train their models, while others may not.
        </p>
        <p>You acknowledge the inherent risks associated with using generative AI, including but not limited to:</p>
        <ul>
          <li>
            <strong>Intellectual property risk:</strong> Outputs may be similar to or inadvertently incorporate existing copyrighted material or trademarks.
            You are responsible for clearing the rights for any Output you use commercially.
          </li>
          <li>
            <strong>Non-uniqueness:</strong> Due to the nature of machine learning, Outputs may not be unique.
            The AI Tools may generate the same or similar Outputs for other Yourshirt users.
            You may not claim any exclusive rights to any Output generated through the AI Tools.
          </li>
          <li>
            <strong>Inaccurate or offensive content:</strong> Outputs may be inaccurate, reflect biases, or contain offensive or inappropriate material.
            Yourshirt is not responsible for screening Outputs.
          </li>
        </ul>
        <p>You are responsible for assessing all risks and making an informed decision before using any AI Tool or its Outputs.</p>

        <h2>4. Limitation of liability and indemnification</h2>
        <p className="text-sm uppercase tracking-wide text-gray-700">
          In no event shall Yourshirt, its officers, directors, agents, affiliates, employees, advertisers, partners, or providers be liable for any indirect, incidental, consequential, or punitive damages
          including, but not limited to, loss of use, loss of profits, or loss of data, whether in an action in contract, tort (including but not limited to negligence), equity or otherwise,
          or for damages in the aggregate exceeding the amount of the fees paid and payable by you to Yourshirt for the use of the specific AI Tool or $100.00 USD (whichever is greater),
          arising out of or in any way connected with the use of the AI Tools.
          Some jurisdictions do not allow the exclusion or limitation of liability, so the above limitations may not apply to you.
        </p>
        <p>
          You agree to indemnify and hold Yourshirt and its parent, subsidiaries, affiliates, partners, and providers (as defined in the Terms of Service) harmless from any claim or demand, including reasonable attorneys&apos; fees,
          made by any third party due to or arising out of your breach of these AI Terms or our other applicable terms, your violation of any law or the rights of a third party, or your use of the AI Tools, your Inputs, or any Outputs generated therefrom.
        </p>

        <h2>5. AI provider usage policies</h2>
        <p>
          Your use of each AI Tool is also subject to the applicable third party&apos;s terms.
          Yourshirt integrates with providers that may change from time to time; non-exhaustive examples include the services you select in the product (e.g. image generation backends).
          You should review each provider&apos;s current terms and acceptable use policy before use.
        </p>
        <ul>
          <li>
            Replace this list with direct links your counsel approves—for example: your AIMLAPI / DeepAI / other vendor policy URLs.
          </li>
        </ul>

        <p className="text-sm text-gray-500 mt-8">
          This page is provided for informational purposes and does not constitute legal advice.
          Have qualified counsel review these terms together with your Terms of Service and storefront flows.
        </p>
    </LegalLayout>
);

export const RefundPolicyPage = () => (
    <LegalLayout title="Refund Policy">
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <p>We want you to be completely satisfied with your purchase. However, because our products are custom-made, we have specific policies regarding repurchases and refunds.</p>

        <h3>1. Damaged or Defective Items</h3>
        <p>If your item arrives damaged or the print quality is poor, please contact us within 30 days of delivery. We will arrange for a free replacement or a full refund.</p>

        <h3>2. Returns for Other Reasons</h3>
        <p>Because every product is custom printed just for you, we cannot offer returns or exchanges for wrong size or buyer's remorse.</p>

        <h3>3. Contact Us</h3>
        <p>If you have any questions about our Refund Policy, please contact us at support@yourshirt.co.</p>
    </LegalLayout>
);
